import { Injectable } from '@angular/core';
import { Observable, catchError, concatMap, defer, from, map, of, switchMap, tap, throwError, toArray } from 'rxjs';
import { SmartintegService } from './smartinteg.service';
import { mensagemErroApi } from '../utils/mensagem-erro-api';
import {
  Comando,
  ComandoMapeamento,
  CriarComandoRequest,
  CriarMapeamentoRequest,
  CriarPublicacaoConfigRequest,
  CriarRecursoRequest,
  PublicacaoConfig,
  Recurso
} from '../types/smartinteg';

type Desfazer = { rotulo: string; acao: () => Observable<unknown> };
type Registrar = (desfazer: Desfazer) => void;

/**
 * Falha ao criar um recurso e seus detalhes. `pendencias` lista o que foi criado e NÃO pôde ser desfeito
 * (a criação não é transacional: são chamadas separadas ao backend).
 */
export class ErroCriacaoRecurso extends Error {
  constructor(readonly causa: any, readonly pendencias: string[]) {
    super(ErroCriacaoRecurso.montarMensagem(causa, pendencias));
    this.name = 'ErroCriacaoRecurso';
    Object.setPrototypeOf(this, ErroCriacaoRecurso.prototype);
  }

  private static montarMensagem(causa: any, pendencias: string[]): string {
    const base = mensagemErroApi(causa, causa?.message ?? 'Erro inesperado');
    if (pendencias.length === 0) return base;
    const lista = pendencias.length === 1 ? pendencias[0] : `${pendencias.slice(0, -1).join(', ')} e ${pendencias[pendencias.length - 1]}`;
    return pendencias.length === 1
      ? `${base} Atenção: o item ${lista} foi criado e não pôde ser removido; remova-o manualmente.`
      : `${base} Atenção: os itens ${lista} foram criados e não puderam ser removidos; remova-os manualmente.`;
  }
}

/**
 * Cria recursos do pipeline pelo caminho genérico (D1): POST /recursos e, em seguida, o detalhe
 * (comando, mapeamento ou config de publicação). As chamadas não são transacionais, então cada passo registra
 * como se desfaz e uma falha desfaz o que já foi criado, do último para o primeiro (filhos antes dos pais).
 * Comandos, mapeamentos e configs criados também são removidos: órfãos continuariam listados e bloqueariam a
 * exclusão de conexões e endpoints pelas verificações de uso.
 */
@Injectable({ providedIn: 'root' })
export class RecursoCriacaoService {

  constructor(private api: SmartintegService) { }

  /** IMPORTACAO, AUTENTICACAO, DATASET e EXPORTACAO por comando: Recurso + Comando */
  criarComComando(recurso: CriarRecursoRequest, comando: CriarComandoRequest): Observable<{ recurso: Recurso; comando: Comando }> {
    return this.executar(registrar => this.recursoComComando(recurso, comando, registrar));
  }

  /** PROCESSAMENTO e VISUALIZACAO: Recurso + Mapeamento (o backend liga Recurso.comandoMapeamento) */
  criarComMapeamento(recurso: CriarRecursoRequest, mapeamento: CriarMapeamentoRequest): Observable<{ recurso: Recurso; mapeamento: ComandoMapeamento }> {
    return this.executar(registrar =>
      this.criarRecurso(recurso, registrar).pipe(
        switchMap(r => this.criarMapeamento(r, mapeamento, registrar).pipe(map(m => ({ recurso: r, mapeamento: m }))))
      ));
  }

  /** PUBLICACAO e EXPORTACAO por config: Recurso + PublicacaoConfig (o backend liga Recurso.publicacaoConfig via recursoId) */
  criarComPublicacao(recurso: CriarRecursoRequest, config: CriarPublicacaoConfigRequest): Observable<{ recurso: Recurso; publicacaoConfig: PublicacaoConfig }> {
    return this.executar(registrar =>
      this.criarRecurso(recurso, registrar).pipe(
        switchMap(r => this.criarPublicacao(r, config, registrar).pipe(map(c => ({ recurso: r, publicacaoConfig: c }))))
      ));
  }

  /**
   * EXPORTACAO: Recurso + Comando de destino + PublicacaoConfig. O comando pertence ao próprio recurso e a config o
   * referencia em comandoDestinoId (junto com o mapeamento de origem em mapeamentoRecebimentoId); por isso a ordem.
   */
  criarExportacao(
    recurso: CriarRecursoRequest,
    comando: CriarComandoRequest,
    config: CriarPublicacaoConfigRequest
  ): Observable<{ recurso: Recurso; comando: Comando; publicacaoConfig: PublicacaoConfig }> {
    return this.executar(registrar =>
      this.recursoComComando(recurso, comando, registrar).pipe(
        switchMap(({ recurso: r, comando: c }) =>
          this.criarPublicacao(r, { ...config, comandoDestinoId: c.id }, registrar).pipe(
            map(cfg => ({ recurso: r, comando: c, publicacaoConfig: cfg }))
          ))
      ));
  }

  /** AGRUPAMENTO, FORMATACAO e JOIN: um único POST (comandoMapeamentoId e configuracoes vão no recurso) */
  criarSimples(recurso: CriarRecursoRequest): Observable<{ recurso: Recurso }> {
    return this.executar(registrar => this.criarRecurso(recurso, registrar).pipe(map(r => ({ recurso: r }))));
  }

  /**
   * ITERACAO em três fases: pai (com o comando da listagem), filho (recursoPaiId, com o seu comando) e, por fim,
   * o PUT do pai com configuracoes.iteracao. O comandoLoop é o id do RECURSO filho, daí a ordem.
   * `campos` mapeia o campo retornado pela listagem para a variável usada no path do filho (ex.: {picId: 'IdPic'}).
   */
  criarIteracao(
    pai: CriarRecursoRequest,
    comandoPai: CriarComandoRequest,
    filho: CriarRecursoRequest,
    comandoFilho: CriarComandoRequest,
    campos: Record<string, string>
  ): Observable<{ pai: Recurso; filho: Recurso; comandoPai: Comando; comandoFilho: Comando }> {
    return this.executar(registrar =>
      this.recursoComComando(pai, comandoPai, registrar).pipe(
        switchMap(({ recurso: rPai, comando: cPai }) =>
          this.recursoComComando({ ...filho, recursoPaiId: rPai.id }, comandoFilho, registrar).pipe(
            switchMap(({ recurso: rFilho, comando: cFilho }) =>
              // O PUT de recurso substitui os campos antigos: reenvia o que acabou de ser criado
              this.api.atualizarRecurso({
                id: rPai.id,
                tipoRecurso: rPai.tipoRecurso,
                nome: rPai.nome,
                descricao: rPai.descricao ?? undefined,
                ordemExecucao: rPai.ordemExecucao,
                codigo: rPai.codigo ?? undefined,
                configuracoes: { ...(rPai.configuracoes ?? {}), iteracao: { comandoLoop: rFilho.id, campos } }
              }).pipe(map(() => ({ pai: rPai, filho: rFilho, comandoPai: cPai, comandoFilho: cFilho })))
            )
          )
        )
      ));
  }

  // ---------- passos ----------

  private recursoComComando(recurso: CriarRecursoRequest, comando: CriarComandoRequest, registrar: Registrar) {
    return this.criarRecurso(recurso, registrar).pipe(
      switchMap(r => this.criarComando(r, comando, registrar).pipe(map(c => ({ recurso: r, comando: c }))))
    );
  }

  private criarRecurso(req: CriarRecursoRequest, registrar: Registrar): Observable<Recurso> {
    return this.api.criarRecurso(req).pipe(
      map(r => this.exigirId(r, 'recurso')),
      tap(r => registrar({ rotulo: `recurso #${r.id}`, acao: () => this.api.deletarRecurso(r.id) }))
    );
  }

  private criarComando(recurso: Recurso, req: CriarComandoRequest, registrar: Registrar): Observable<Comando> {
    // POST /comandos exige ordem e um comando por recurso; o outro identificador (conexaoId/endpointId) é ignorado
    return this.api.criarComando({ ...req, recursoId: recurso.id, ordem: req.ordem ?? 1 }).pipe(
      map(c => this.exigirId(c, 'comando')),
      tap(c => registrar({ rotulo: `comando #${c.id}`, acao: () => this.api.deletarComando(c.id) }))
    );
  }

  private criarMapeamento(recurso: Recurso, req: CriarMapeamentoRequest, registrar: Registrar): Observable<ComandoMapeamento> {
    // "mapeamentos" é obrigatório no backend mesmo quando vazio
    return this.api.criarMapeamento({ ...req, mapeamentos: req.mapeamentos ?? {}, recursoId: recurso.id }).pipe(
      map(m => this.exigirId(m, 'mapeamento')),
      tap(m => registrar({ rotulo: `mapeamento #${m.id}`, acao: () => this.api.deletarMapeamento(m.id) }))
    );
  }

  private criarPublicacao(recurso: Recurso, req: CriarPublicacaoConfigRequest, registrar: Registrar): Observable<PublicacaoConfig> {
    return this.api.criarPublicacao({ ...req, recursoId: recurso.id }).pipe(
      map(c => this.exigirId(c, 'configuração de publicação')),
      tap(c => registrar({ rotulo: `configuração de publicação #${c.id}`, acao: () => this.api.deletarPublicacao(c.id) }))
    );
  }

  private exigirId<T extends { id: number }>(resposta: T | null | undefined, o_que: string): T {
    if (!resposta || resposta.id === undefined || resposta.id === null) {
      throw new Error(`O backend não devolveu o id do ${o_que} criado.`);
    }
    return resposta;
  }

  // ---------- desfazer ----------

  private executar<T>(passos: (registrar: Registrar) => Observable<T>): Observable<T> {
    const criados: Desfazer[] = [];
    return defer(() => passos(d => criados.push(d))).pipe(
      catchError(erro => this.desfazer(criados, erro))
    );
  }

  private desfazer(criados: Desfazer[], erro: any): Observable<never> {
    if (criados.length === 0) {
      return throwError(() => new ErroCriacaoRecurso(erro, []));
    }
    return from([...criados].reverse()).pipe(
      // Uma falha ao desfazer um item não interrompe os demais: vira pendência para o usuário
      concatMap(d => d.acao().pipe(map(() => null as string | null), catchError(() => of(d.rotulo)))),
      toArray(),
      switchMap(resultados => throwError(() => new ErroCriacaoRecurso(erro, resultados.filter((r): r is string => r !== null))))
    );
  }
}
